import { selectors } from "../add-element/cypressSelectors";
import { EditableResourceFields } from "../resources/modelTypes";
import { format as formatDate } from "date-fns";
import React from "react";

export const formatAddress = ({
  address1,
  address2,
  city,
  state,
  zipCode,
}: EditableResourceFields) => {
  // Return empty string if all required fields are null/undefined/empty
  if (!address1 && !city && !state && !zipCode) {
    return "";
  }

  // Filter and join the comma-separated parts: address1, address2?, city, state
  const addressParts = [address1, address2, city, state].filter(Boolean);

  let result = addressParts.join(", ");

  // Handle zipCode separately (space-separated from state)
  if (zipCode) {
    result += ` ${zipCode}`;
  }

  return result;
};

export const formatEventDate = (date: string) => {
  // incoming date format: 'MM/dd/yyyy'
  const [month, day, year] = date.split("/");

  if (!(day && month && year)) {
    return "";
  }

  const actualDate = new Date(Number(year), Number(month) - 1, Number(day));

  return formatDate(actualDate, "EEEE, MMMM d, yyyy");
};

/**
 * Get timezone abbreviation (e.g., "EST", "PST", "CDT") from IANA timezone string.
 */
export const getTimezoneAbbreviation = (
  timezone: string | null | undefined,
): string => {
  if (!timezone) {
    return "";
  }

  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "short",
    }).formatToParts(new Date());

    return parts.find((part) => part.type === "timeZoneName")?.value || "";
  } catch {
    return "";
  }
};

export const formatEventTime = (
  date: string,
  time: string,
  timezone?: string | null,
) => {
  const timezoneAbbr = getTimezoneAbbreviation(timezone);
  const timeDisplay = timezoneAbbr ? `${time} ${timezoneAbbr}` : time;

  return (
    <div>
      {date}
      <br />
      {timeDisplay}
    </div>
  );
};

export const getEventDateTimeComponent = (
  date: string | undefined,
  time: string | undefined,
  endDate: string | undefined,
  endTime: string | undefined,
  timezone?: string | null,
) => {
  // Incoming date format: 'MM/dd/yyyy'

  // Date is required to be valued
  if (!date) {
    return null;
  }

  const formattedStartDate = formatEventDate(date);
  const timezoneAbbr = getTimezoneAbbreviation(timezone);

  const startDateTime = time
    ? formatEventTime(formattedStartDate, time, timezone)
    : formattedStartDate;

  if (!endDate) {
    return (
      <div data-cy={`${selectors.sections_collapsedField}_datetime`}>
        {startDateTime}
      </div>
    );
  }

  if (date === endDate && time !== endTime) {
    const timeWithTz = timezoneAbbr
      ? `${time} - ${endTime} ${timezoneAbbr}`
      : `${time} - ${endTime}`;
    const timeRange = endTime ? (
      <span>
        <br />
        {timeWithTz}
      </span>
    ) : timezoneAbbr ? (
      `${time} ${timezoneAbbr}`
    ) : (
      time
    );

    return (
      <div data-cy={`${selectors.sections_collapsedField}_datetime`}>
        {`${formattedStartDate}`}
        {timeRange}
      </div>
    );
  }

  if (date !== endDate) {
    const formattedEndDate = formatEventDate(endDate);
    const endDateTime = endTime
      ? formatEventTime(formattedEndDate, endTime, timezone)
      : formattedEndDate;

    return (
      <div data-cy={`${selectors.sections_collapsedField}_datetime`}>
        {startDateTime}
        -
        <br />
        {endDateTime}
      </div>
    );
  }

  return (
    <div data-cy={`${selectors.sections_collapsedField}_datetime`}>
      {startDateTime}
    </div>
  );
};
